<template>
  <table>
    <thead :style="{ width: `calc(100% - ${scrollBarWidth}px)` }">
      <template v-for="column in columns">
        <th
          v-if="!column.hidden"
          :key="column.key"
          :class="[column.key, { sortable: column.sortable }]"
          @click="() => column.sortable && $emit('sort', column.key)"
        >
          <span>{{ column.name !== undefined ? column.name : column.key }}</span>
          <SortButton v-if="column.sortable" :direction="column.key === sortKey ? sortDirection : null" />
        </th>
      </template>
      <th v-if="editButton" class="action edit">編集</th>
      <th v-if="copyButton" class="action copy">複製</th>
      <th v-if="deleteButton" class="action delete">削除</th>
    </thead>
    <LoadingIndicator v-if="loading && (!scrollOver || loadInitial)" />
    <div v-else-if="!loading && !data.length" class="nodata">
      <!-- @slot データがない場合の表示を置き換えます -->
      <slot name="nodata">{{ nodata }}</slot>
    </div>
    <DynamicScroller
      v-if="data"
      ref="main"
      v-bind="$attrs"
      :items="data"
      :min-item-size="minItemHeight"
      v-scroll-over="scrollOver"
    >
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem class="row" :item="item" :active="active" :data-index="index" watchData emitResize>
          <!-- @slot 行の前に表示されます -->
          <slot name="before-row" :item="item" />
          <tr
            :class="[getItemClass(item), { selectable }]"
            :style="{ 'min-height': `${minItemHeight - 1}px` }"
            @click="() => selectable && $emit('select', item, index)"
          >
            <template v-for="column in columns">
              <td v-if="!column.hidden" :key="column.key" :class="column.key">
                <slot :name="column.key" :item="item" v-bind="item">
                  <template v-if="column.format">{{ column.format(item[column.key]) }}</template>
                  <template v-else-if="column.filter">{{ column.filter(item) }}</template>
                  <template v-else>{{ item[column.key] }}</template>
                </slot>
              </td>
            </template>
            <td v-if="editButton" class="action edit">
              <Button icon :disabled="editDisabled(item)" @click.stop="() => $emit('edit', item, index)">
                <EditIcon />
              </Button>
            </td>
            <td v-if="copyButton" class="action copy">
              <Button icon :disabled="copyDisabled(item)" @click.stop="() => $emit('copy', item, index)">
                <CopyIcon />
              </Button>
            </td>
            <td v-if="deleteButton" class="action delete">
              <Button icon :disabled="deleteDisabled(item)" @click.stop="() => $emit('delete', item, index)">
                <DeleteIcon />
              </Button>
            </td>
            <slot name="extend" :item="item" />
          </tr>
          <!-- @slot 行の後ろに表示されます -->
          <slot name="after-row" :item="item" />
        </DynamicScrollerItem>
      </template>
      <template v-if="loading && scrollOver && !loadInitial" #after>
        <LoadingIndicator small />
      </template>
      <!-- @slot テーブルの後ろに表示されます -->
      <slot name="after" slot="after" />
    </DynamicScroller>
  </table>
</template>

<script>
import { isFunction } from 'lodash'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import { DynamicScroller, DynamicScrollerItem } from '@/components/molecules/VirtualScroller'
import Button from '@/components/atoms/Button'
import SortButton from '@/components/atoms/SortButton'
import CopyIcon from '@/components/icons/copy'
import DeleteIcon from '@/components/icons/delete'
import EditIcon from '@/components/icons/edit'
import ScrollOver from '@/directives/ScrollOver'

/**
 * データテーブル
 */
export default {
  name: 'DataTable',
  components: {
    LoadingIndicator,
    DynamicScroller,
    DynamicScrollerItem,
    Button,
    SortButton,
    CopyIcon,
    DeleteIcon,
    EditIcon,
  },
  directives: {
    ScrollOver,
  },
  props: {
    /** 列情報 { key: 行データのキー, name: 列名, sortable: ソート可能, hidden: 非表示 } */
    columns: Array,
    /** 行データ */
    data: { type: Array, default: () => [] },
    /** ローディング中フラグ */
    loading: Boolean,
    /** ソート対象のキー */
    sortKey: String,
    /** ソート方向 up/down */
    sortDirection: String,
    /** 選択可能フラグ */
    selectable: Boolean,
    /** 編集ボタンの表示。関数を渡した場合は、戻り値(boolean)により行ごとの活性／非活性を指定します */
    editButton: [Boolean, Function],
    /** 複製ボタンの表示。関数を渡した場合は、戻り値(boolean)により行ごとの活性／非活性を指定します */
    copyButton: [Boolean, Function],
    /** 削除ボタンの表示。関数を渡した場合は、戻り値(boolean)により行ごとの活性／非活性を指定します */
    deleteButton: [Boolean, Function],
    /** データがない場合に表示する文言 */
    nodata: { type: String, default: '該当するデータはありません' },
    /** 最小の行の高さ */
    minItemHeight: { type: Number, default: 44 },
    /** 行のクラス名 */
    itemClass: [String, Function],
    /** ScrollOver 設定 */
    scrollOver: [Function, Object],
    /** loadInitial 設定 */
    loadInitial: Boolean,
  },
  data() {
    return {
      scrollBarWidth: 0,
    }
  },
  updated() {
    this.$nextTick(() => {
      const main = this.$refs.main
      this.scrollBarWidth = main ? main.$el.offsetWidth - main.$el.clientWidth : 0
    })
  },
  methods: {
    editDisabled(item) {
      return isFunction(this.editButton) && !this.editButton(item)
    },
    copyDisabled(item) {
      return isFunction(this.copyButton) && !this.copyButton(item)
    },
    deleteDisabled(item) {
      return isFunction(this.deleteButton) && !this.deleteButton(item)
    },
    getItemClass(item) {
      return isFunction(this.itemClass) ? this.itemClass(item) : this.itemClass
    },
  },
}
</script>

<style lang="postcss" scoped>
table {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 150px;
  word-break: break-all;
}

thead {
  align-items: center;
  background: var(--very-dark-gray);
  box-sizing: border-box;
  color: var(--white);
  display: flex;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  position: sticky;
  top: 0;
}

.row {
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

tr {
  background: var(--white);
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;

  &.selectable {
    cursor: pointer;
  }

  &.disabled {
    color: var(--light-gray);
  }
}

th,
td {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  line-height: 1;
  position: relative;
  width: 100%;

  >>> svg {
    flex-basis: 24px;
    flex-shrink: 0;
  }
}

th {
  height: 100%;
  justify-content: center;
  min-height: 32px;

  &:not(:last-child) {
    border-right: 1px solid var(--white);
  }

  &.action {
    border-right: none;
  }

  &.sortable {
    cursor: pointer;
  }
}

td {
  line-height: 1.4;
  padding: 0 24px;

  &:not(:last-child)::after {
    background-image: linear-gradient(
      to bottom,
      var(--dark-gray),
      var(--dark-gray) 1px,
      transparent 1px,
      transparent 4px
    );
    background-repeat: repeat-y;
    background-size: 1px 5px;
    bottom: 1px;
    content: '';
    position: absolute;
    right: 0;
    top: 1px;
    width: 1px;
  }

  &.action {
    justify-content: center;

    .disabled {
      fill: var(--light-gray);
    }

    &::after {
      display: none;
    }
  }
}

.action {
  fill: var(--moderate-blue);
  justify-content: center;
  min-width: 52px;
  padding: 0;
  width: 52px;
}

.nodata {
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
}
</style>
